import React from 'react';
import { Field, FieldProps } from 'formik';
import { tv } from 'tailwind-variants';
import { FormFieldWrapper } from './FormFieldWrapper';
import { CustomForm } from '.generated/Feature.Irwinmitchell.model';
import { Field as SitecoreField } from '@sitecore-content-sdk/nextjs';

const checkboxGroupStyles = tv({
  base: ['flex', 'flex-col', 'gap-spacing-spacing-24'],
});

const checkboxOptionStyles = tv({
  base: ['flex', 'items-center', 'gap-spacing-spacing-16', 'cursor-pointer', 'group'],
  variants: {
    state: {
      default: '',
      error: '',
      disabled: ['opacity-40', 'cursor-not-allowed'],
    },
  },
  defaultVariants: {
    state: 'default',
  },
});

const checkboxInputWrapperStyles = tv({
  base: [
    'w-[28px]',
    'h-[28px]',
    'border',
    'border-color-input-border',
    'bg-color-grayscale-white',
    'cursor-pointer',
    'flex-shrink-0',
    'flex',
    'items-center',
    'justify-center',
    'transition-all',
    'duration-200',
    'hover:border-color-input-border-hover',
    'relative',
  ],
  variants: {
    state: {
      default: '',
      error: [
        'border-color-text-text-critical',
        'focus-within:border-color-text-text-error',
        'focus-within:ring-color-text-text-error',
      ],
      disabled: ['bg-color-grayscale-gray-100', 'cursor-not-allowed', 'opacity-40'],
    },
    checked: {
      true: ['border-color-input-border'],
      false: '',
    },
  },
  defaultVariants: {
    state: 'default',
    checked: false,
  },
});

const checkboxInputStyles = tv({
  base: ['sr-only'],
});

const checkboxDotStyles = tv({
  base: [
    'transition-opacity',
    'duration-200',
    'w-2',
    'h-4',
    'border-b-[3px]',
    'border-r-[3px]',
    'border-color-core-color-4-SkyBlue',
    'transform',
    'rotate-45',
    'translate-y-[-2px]',
  ],
  variants: {
    checked: {
      true: ['opacity-100'],
      false: ['opacity-0'],
    },
    state: {
      default: '',
      error: ['bg-color-text-text-error'],
      disabled: '',
    },
  },
  defaultVariants: {
    checked: false,
    state: 'default',
  },
});

const checkboxLabelStyles = tv({
  base: [
    'text-color-core-color-1-Navy',
    'font-typography-font-family-body',
    'text-typography-font-size-body-medium',
    'leading-typography-line-height-body-medium',
    'font-normal',
    'cursor-pointer',
    'select-none',
  ],
  variants: {
    state: {
      default: '',
      error: 'text-color-text-text-critical',
      disabled: ['text-color-text-text-disabled', 'cursor-not-allowed'],
    },
  },
  defaultVariants: {
    state: 'default',
  },
});

interface CheckboxOption {
  value: string;
  label: string;
}

interface CheckboxFieldProps {
  field: CustomForm.FormField_Item;
  hasError?: boolean;
  isTouched?: boolean;
  errors?: Record<string, string | undefined>;
  touched?: Record<string, boolean | undefined>;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  hideLabel?: boolean;
  isStandalone?: boolean;
}

// Standalone checkbox component that works without Formik
const CheckboxFieldStandalone: React.FC<{
  fieldName: string;
  options?: CheckboxOption[];
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  isDisabled?: boolean;
  hasError?: boolean;
  'aria-required'?: boolean;
}> = ({
  fieldName,
  options,
  value = '',
  onChange,
  onBlur,
  isDisabled = false,
  hasError = false,
  'aria-required': ariaRequired = false,
}) => {
  const fieldState = isDisabled ? 'disabled' : hasError ? 'error' : 'default';
  const checkboxOptions = options ?? [];

  const handleChange = (optionValue: string) => {
    if (onChange) {
      const nextValue = optionValue === value ? '' : optionValue;
      onChange(nextValue);
    }
  };

  return (
    <div className={checkboxGroupStyles()} role="checkboxgroup" aria-required={ariaRequired}>
      {checkboxOptions.map((option) => {
        const optionId = `${fieldName}-${option.value}`;
        const isChecked = value === option.value;

        return (
          <label
            key={option.value}
            htmlFor={optionId}
            className={checkboxOptionStyles({ state: fieldState })}
          >
            <div
              className={checkboxInputWrapperStyles({
                state: fieldState,
                checked: isChecked,
              })}
            >
              <input
                type="checkbox"
                name={fieldName}
                id={optionId}
                value={option.value}
                checked={isChecked}
                onChange={() => handleChange(option.value)}
                onBlur={onBlur}
                disabled={isDisabled}
                className={checkboxInputStyles()}
                aria-describedby={hasError ? `${fieldName}-error` : undefined}
                data-automation={`checkboxField-${fieldName}-${option.value}`}
              />
              <div
                className={checkboxDotStyles({
                  checked: isChecked,
                  state: fieldState,
                })}
                aria-hidden="true"
              />
            </div>
            <span className={checkboxLabelStyles({ state: fieldState })}>{option.label}</span>
          </label>
        );
      })}
    </div>
  );
};

// Custom Formik Field component for checkboxes
const CustomCheckboxField: React.FC<
  FieldProps & {
    options: CheckboxOption[];
    isDisabled?: boolean;
    hasError?: boolean;
    'aria-required'?: boolean;
    fieldName: string;
  }
> = ({
  field,
  form,
  options,
  isDisabled = false,
  hasError = false,
  'aria-required': ariaRequired = false,
  fieldName,
}) => {
  const fieldState = isDisabled ? 'disabled' : hasError ? 'error' : 'default';
  const checkboxOptions = options ?? [];

  const handleChange = async (value: string) => {
    try {
      const nextValue = value === field.value ? '' : value;
      await form.setFieldValue(field.name, nextValue, false);
      await form.setFieldTouched(field.name, true, true);
    } catch (error) {
      console.error('Error updating checkbox field:', field.name, error);
      const nextValue = value === field.value ? '' : value;
      form.setFieldValue(field.name, nextValue, false);
    }
  };

  const handleBlur = () => {
    form.setFieldTouched(field.name, true, true);
  };

  return (
    <div className={checkboxGroupStyles()} role="checkboxgroup" aria-required={ariaRequired}>
      {checkboxOptions.map((option) => {
        const optionId = `${fieldName}-${option.value}`;
        const isChecked = field.value === option.value;

        return (
          <label
            key={option.value}
            htmlFor={optionId}
            className={checkboxOptionStyles({ state: fieldState })}
          >
            <div
              className={checkboxInputWrapperStyles({
                state: fieldState,
                checked: isChecked,
              })}
            >
              <Field
                type="checkbox"
                name={field.name}
                id={optionId}
                value={option.value}
                checked={isChecked}
                onChange={() => handleChange(option.value)}
                onBlur={handleBlur}
                disabled={isDisabled}
                className={checkboxInputStyles()}
                aria-required={ariaRequired}
                aria-describedby={hasError ? `${fieldName}-error` : undefined}
                data-automation={`checkboxField-${fieldName}-${option.value}`}
              />
              <div
                className={checkboxDotStyles({
                  checked: isChecked,
                  state: fieldState,
                })}
                aria-hidden="true"
              />
            </div>
            <span className={checkboxLabelStyles({ state: fieldState })}>{option.label}</span>
          </label>
        );
      })}
    </div>
  );
};

export const CheckboxField: React.FC<CheckboxFieldProps> = ({
  field,
  hasError = false,
  errors,
  touched,
  value,
  onChange,
  onBlur,
  hideLabel,
  isStandalone = false,
}) => {
  const fieldConfig = field.fields;
  const fieldName = fieldConfig?.fieldName?.value || fieldConfig?.fieldId?.value;
  const label = fieldConfig?.label?.value || fieldName || '';
  const helpText = fieldConfig?.helpText?.value;
  const disabled = fieldConfig?.disabled?.value;
  const required = fieldConfig?.required?.value;

  if (!fieldName) return null;

  // Transform Sitecore options to checkbox format
  const optionsFromConfig: CheckboxOption[] = (fieldConfig?.selectOptionsAndSection || []).map(
    (option) => ({
      value: (option.fields?.value as SitecoreField<string>)?.value || '',
      label: (option.fields?.text as SitecoreField<string>)?.value || '',
    })
  );
  const resolvedOptions =
    optionsFromConfig.length > 0 ? optionsFromConfig : [{ value: 'true', label }];

  return (
    <FormFieldWrapper
      key={fieldName}
      fieldName={fieldName}
      label={label}
      helpText={helpText}
      isDisabled={disabled}
      hasError={hasError}
      isRequired={required}
      errors={errors}
      touched={touched}
      hideLabel={hideLabel}
    >
      {isStandalone ? (
        <CheckboxFieldStandalone
          fieldName={fieldName}
          options={resolvedOptions}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          isDisabled={disabled}
          hasError={hasError}
          aria-required={required}
        />
      ) : (
        <Field
          name={fieldName}
          component={CustomCheckboxField}
          options={resolvedOptions}
          isDisabled={disabled}
          hasError={hasError}
          aria-required={required}
          fieldName={fieldName}
        />
      )}
    </FormFieldWrapper>
  );
};

export default CheckboxField;
